import { Box, Button, useTheme } from '@mui/material';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';

import { useBreakpoints } from '@frontend/features/common/hooks/useBreakpoints';
import { routes } from '@frontend/router/routes';
import { useGetHotelNimbuxHotelById } from '@frontend/features/hotel-content-manager/queries';

import { HotelPropertySearchDTO, LocationSelection, RoomSelection } from './types';
import { HotelSearchInput } from './HotelSearchInput';
import { HotelSearchRoomInput } from './HotelSearchRoomInput';
import { HotelSearchCalendar } from './HotelSearchCalendar';

type HotelSearchType = 'location' | 'property';

export const HotelSearchBar = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMediumUp } = useBreakpoints();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {hotelId} = useParams<{ hotelId: string }>();
  const location = useLocation();
  
  const [minToDate] = useState(() => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 1);
    return minDate;
  });

  const [fromDate, setFromDate] = useState<Date | null>();
  const [toDate, setToDate] = useState<Date | null>();
  const [rooms, setRooms] = useState<RoomSelection[] | null>([
    { paxes: [30, 30] }
  ]);
  const [locationState, setLocationState] = useState<LocationSelection | null>(null);
  const [property, setProperty] = useState<HotelPropertySearchDTO | null>(null);
  const [searchType, setType] = useState<HotelSearchType>('location');
  
  // Obtener info del hotel si estamos en la ruta de property fares
  const { data: hotelData } = useGetHotelNimbuxHotelById(
    hotelId ? Number(hotelId) : undefined
  );

  const searchData = useMemo(
    () => ({
      location: locationState || null,
      from: fromDate || null,
      to: toDate || null,
      rooms: rooms || null,
      property: property || null,
    }),
    [locationState, fromDate, toDate, rooms, property],
  );

  useEffect(() => {
    const isPropertyFaresRoute = location.pathname.includes('/hotel-fares/property/');
    
    if (isPropertyFaresRoute && hotelId && hotelData) {
      setType('property');
      
      setProperty({
        hotelName: hotelData.name || '',
        nimbuxHotelId: hotelId as string,
      });
      
      const checkinParam = searchParams.get('checkin');
      const checkoutParam = searchParams.get('checkout');
      
      if (checkinParam) {
        setFromDate(new Date(checkinParam));
      }
      
      if (checkoutParam) {
        setToDate(new Date(checkoutParam));
      }
      
      const roomsParam = searchParams.get('rooms');
      if (roomsParam) {
        
        const parsedRooms = roomsParam.split('|').map(room => ({
          paxes: room.split(',').map(age => Number(age))
        }));
        setRooms(parsedRooms);
      }
    }
  }, [hotelId, hotelData, searchParams, location.pathname]);

  const handleFromDateChange = (newFromDate: Date) => {
    setFromDate(newFromDate);
    if (toDate && newFromDate >= toDate) {
      const newToDate = new Date(newFromDate);
      newToDate.setDate(newToDate.getDate() + 1);
      setToDate(newToDate);
    }
  };

  const handleToDateChange = (newToDate: Date) => {
    setToDate(newToDate);
    if (fromDate && newToDate <= fromDate) {
      const newFromDate = new Date(newToDate);
      newFromDate.setDate(newFromDate.getDate() - 1);
      setFromDate(newFromDate);
    }
  };


  const handleSubmitSearch = () => {
    const checkin = fromDate?.toISOString().split('T')[0];
    const checkout = toDate?.toISOString().split('T')[0];
    const roomsParam = rooms?.map(room => room.paxes.join(',')).join('|');
    const queryParams = new URLSearchParams();
    checkin && queryParams.append('checkin', checkin);
    checkout && queryParams.append('checkout', checkout);
    roomsParam && queryParams.append('rooms', roomsParam);

   if (searchType === 'location') {
    alert(`Search by location ain't ready yet`);
   } else if (searchType === 'property' && property && fromDate && toDate && rooms) {
     const url = routes.hotelPropertyFares.replace(':hotelId', property.nimbuxHotelId);
     navigate(`${url}?${queryParams.toString()}`);
   }
  };

  return (
    <>
      <Box
        display="flex"
        position="relative"
        width="100%"
        flexWrap={isMediumUp ? 'nowrap' : 'wrap'}
        gap={theme.spacing(2)}
      >
        <HotelSearchInput
          setSearchLocation={setLocationState}
          setSearchProperty={setProperty}
          setSearchType={setType}
          searchData={searchData}
        />
        <HotelSearchCalendar
          identifier='checkIn'
          value={searchData.from}
          onChange={handleFromDateChange}
        />
        <HotelSearchCalendar
          minDate={minToDate}
          identifier='checkOut'
          value={searchData.to}
          onChange={handleToDateChange}
        />
        <Box minWidth={280}>
          <HotelSearchRoomInput
            setSearchRooms={setRooms}
            searchData={searchData}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="end"
        width="100%"
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmitSearch}
          disabled={
            (
              !searchData.location &&
              !searchData.property?.nimbuxHotelId
            ) ||
            !searchData.from ||
            !searchData.to ||
            !searchData.rooms
          }
        >
          {t(location.pathname === routes.hotels.home ? 'hotels.searcher.search' : 'common.form.apply')}
        </Button>
      </Box>
    </>
  );
};
